/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        AI Cinematic Video Studio Complete View (Task 10.4.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef } from 'react';
import {
  Sparkles,
  UploadCloud,
  Image as ImageIcon,
  Check,
  AlertCircle,
  Copy,
  Trash2,
  Repeat,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ZoomIn,
  ZoomOut,
  RotateCw,
  RotateCcw,
  Play,
  X,
  UserCheck,
} from 'lucide-react';
import { SophiaIcon } from '@sofia/assets';
import { useTranslation } from '../i18n';
import {
  useVideoStudioStore,
  VIDEO_5S_COST,
  VIDEO_10S_COST,
  VIDEO_MODELS,
  SAMPLE_KEYFRAMES,
  SAMPLE_PROMPT_PRESETS,
  CameraDirection,
  VideoModelId,
  VideoAspectRatio,
} from '../stores/useVideoStudioStore';
import { CustomVideoPlayer } from '../components/media/CustomVideoPlayer';

interface VideoStudioViewProps {
  onNavigateToGallery?: () => void;
  onNavigateToAvatar?: () => void;
}

export const VideoStudioView: React.FC<VideoStudioViewProps> = ({
  onNavigateToGallery,
  onNavigateToAvatar,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    mode,
    prompt,
    negativePrompt,
    keyframeUrl,
    model,
    duration,
    aspectRatio,
    cameraDirection,
    motionIntensity,
    loop,
    isGenerating,
    isEnhancingPrompt,
    generationProgress,
    generationStage,
    errorMessage,
    activeVideo,
    generatedVideos,
    setMode,
    setPrompt,
    setNegativePrompt,
    setKeyframeUrl,
    setModel,
    setDuration,
    setAspectRatio,
    setCameraDirection,
    setMotionIntensity,
    setLoop,
    setActiveVideo,
    applyPreset,
    enhancePromptWithAI,
    generateVideo,
    deleteVideo,
    resetStudio,
  } = useVideoStudioStore();

  const [copiedPrompt, setCopiedPrompt] = useState<boolean>(false);
  const [showNegative, setShowNegative] = useState<boolean>(false);
  const [showPresets, setShowPresets] = useState<boolean>(true);

  const requiredCredits = duration === 10 ? VIDEO_10S_COST : VIDEO_5S_COST;

  // Handle local image file upload for Image-to-Video Keyframe
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      if (result) {
        setKeyframeUrl(result);
      }
    };
    reader.readAsDataURL(file);
  };

  // Copy active prompt to clipboard
  const handleCopyPrompt = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedPrompt(true);
    setTimeout(() => setCopiedPrompt(false), 2000);
  };

  // Camera direction options with icons
  const cameraDirections: { id: CameraDirection; labelKey: string; icon: React.FC<{ className?: string }> }[] = [
    { id: 'static', labelKey: 'video.cameraDirectionStatic', icon: (props) => <SophiaIcon name="camera-360" size={14} {...props} /> },
    { id: 'zoom-in', labelKey: 'video.cameraDirectionZoomIn', icon: ZoomIn },
    { id: 'zoom-out', labelKey: 'video.cameraDirectionZoomOut', icon: ZoomOut },
    { id: 'pan-left', labelKey: 'video.cameraDirectionPanLeft', icon: ArrowLeft },
    { id: 'pan-right', labelKey: 'video.cameraDirectionPanRight', icon: ArrowRight },
    { id: 'tilt-up', labelKey: 'video.cameraDirectionTiltUp', icon: ArrowUp },
    { id: 'tilt-down', labelKey: 'video.cameraDirectionTiltDown', icon: ArrowDown },
    { id: 'orbit', labelKey: 'video.cameraDirectionOrbit', icon: RotateCw },
  ];

  return (
    <div className="space-y-6">
      {/* 1. Header Banner & Mode Selector */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 sm:p-6 rounded-2xl border border-surface-border bg-surface shadow-glass">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
              <SophiaIcon name="camera-360" size={18} className="text-primary shrink-0" />
            </div>
            <h2 className="text-xl sm:text-2xl font-heading font-extrabold text-foreground tracking-tight">
              {t('video.title')}
            </h2>
          </div>
          <p className="text-foreground-muted text-xs sm:text-sm max-w-2xl">
            {t('video.subtitle')}
          </p>
        </div>

        {/* Header Right Actions: Mode Switcher & Reset */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center p-1 bg-surface-elevated border border-surface-border rounded-xl">
            <button
              onClick={() => setMode('text-to-video')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all ${
                mode === 'text-to-video'
                  ? 'bg-primary text-primary-fg shadow-glow-primary/20'
                  : 'text-foreground-muted hover:text-foreground hover:bg-muted/40'
              }`}
            >
              <SophiaIcon name="camera-360" size={14} className="shrink-0" />
              <span>{t('video.textToVideoTab')}</span>
            </button>
            <button
              onClick={() => setMode('image-to-video')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all ${
                mode === 'image-to-video'
                  ? 'bg-primary text-primary-fg shadow-glow-primary/20'
                  : 'text-foreground-muted hover:text-foreground hover:bg-muted/40'
              }`}
            >
              <SophiaIcon name="vision" size={14} className="shrink-0" />
              <span>{t('video.imageToVideoTab')}</span>
            </button>
          </div>

          {onNavigateToAvatar && (
            <button
              type="button"
              onClick={onNavigateToAvatar}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated hover:bg-muted/60 text-xs font-semibold text-foreground transition-all"
            >
              <UserCheck className="w-3.5 h-3.5 text-primary" />
              <span>{t('avatar.title')}</span>
            </button>
          )}

          <button
            type="button"
            onClick={resetStudio}
            className="p-2.5 rounded-xl border border-surface-border bg-surface-elevated hover:bg-muted/60 text-foreground-muted hover:text-foreground transition-colors"
            title="Reset studio parameters"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 2. Error Message Banner */}
      {errorMessage && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400 text-sm animate-in fade-in">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1 font-medium">{errorMessage}</div>
          <button
            onClick={() => useVideoStudioStore.setState({ errorMessage: null })}
            className="p-1 rounded-lg hover:bg-rose-500/20 text-rose-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 3. Main Workspace Grid: Controls & Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Studio Controls (7 cols on lg) */}
        <div className="lg:col-span-7 space-y-6">
          {/* Card A: Keyframe Selector (Displayed in image-to-video mode) */}
          {mode === 'image-to-video' && (
            <div className="p-5 rounded-2xl border border-surface-border bg-surface space-y-4 shadow-glass">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-bold text-sm sm:text-base text-foreground">
                    {t('video.keyframeLabel')}
                  </h3>
                  <p className="text-foreground-muted text-xs">
                    {t('video.keyframeDesc')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {onNavigateToGallery && (
                    <button
                      type="button"
                      onClick={onNavigateToGallery}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-surface-border bg-surface-elevated hover:bg-muted/60 text-xs font-semibold text-foreground transition-colors"
                    >
                      <ImageIcon className="w-3.5 h-3.5 text-primary" />
                      <span>{t('video.chooseFromGallery')}</span>
                    </button>
                  )}
                  {keyframeUrl && (
                    <button
                      onClick={() => setKeyframeUrl(null)}
                      className="flex items-center gap-1 text-xs text-rose-400 hover:text-rose-300 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>{t('video.removeKeyframe')}</span>
                    </button>
                  )}
                </div>
              </div>

              {keyframeUrl ? (
                <div className="relative rounded-xl overflow-hidden border border-surface-border bg-black/40 aspect-video flex items-center justify-center group">
                  <img
                    src={keyframeUrl}
                    alt="Keyframe preview"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-xs font-medium backdrop-blur-md transition-colors"
                    >
                      {t('video.uploadKeyframeBtn')}
                    </button>
                    <button
                      onClick={() => setKeyframeUrl(null)}
                      className="px-3 py-1.5 rounded-lg bg-rose-500/40 hover:bg-rose-500/60 text-white text-xs font-medium backdrop-blur-md transition-colors"
                    >
                      {t('video.removeKeyframe')}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-surface-border hover:border-primary/50 bg-surface-elevated/40 hover:bg-surface-elevated/80 rounded-xl p-6 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all text-center"
                >
                  <UploadCloud className="w-8 h-8 text-primary" />
                  <span className="text-xs sm:text-sm font-semibold text-foreground">
                    {t('video.uploadKeyframeBtn')}
                  </span>
                  <span className="text-[11px] text-foreground-muted">
                    {t('video.keyframeDesc')}
                  </span>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleFileUpload}
                className="hidden"
              />

              {/* Sample Keyframes Bar */}
              <div className="space-y-2 pt-1">
                <span className="text-xs font-medium text-foreground-muted">
                  {t('video.sampleKeyframesTitle')}:
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {SAMPLE_KEYFRAMES.map((kf) => (
                    <button
                      key={kf.id}
                      onClick={() => setKeyframeUrl(kf.url)}
                      className={`relative aspect-video rounded-lg overflow-hidden border transition-all group ${
                        keyframeUrl === kf.url
                          ? 'border-primary ring-2 ring-primary/40'
                          : 'border-surface-border hover:border-surface-border/80'
                      }`}
                    >
                      <img
                        src={kf.url}
                        alt={kf.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-x-0 bottom-0 p-1 bg-black/60 text-[9px] text-white/90 truncate text-center">
                        {kf.title}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Card B: Prompt & AI Enhancement */}
          <div className="p-5 rounded-2xl border border-surface-border bg-surface space-y-4 shadow-glass">
            <div className="flex items-center justify-between">
              <label className="font-heading font-bold text-sm sm:text-base text-foreground">
                {t('video.promptLabel')}
              </label>
              <button
                onClick={enhancePromptWithAI}
                disabled={isEnhancingPrompt || !prompt.trim()}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-accent/15 text-accent border border-accent/25 hover:bg-accent/25 transition-colors disabled:opacity-50"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>
                  {isEnhancingPrompt
                    ? t('video.enhancingPrompt')
                    : t('video.enhancePromptBtn')}
                </span>
              </button>
            </div>

            <textarea
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t('video.promptPlaceholder')}
              className="w-full px-3.5 py-2.5 rounded-xl border border-surface-border bg-surface-elevated text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all resize-y"
            />

            {/* Negative Prompt Accordion */}
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowNegative(!showNegative)}
                className="flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground font-medium"
              >
                <span>{showNegative ? '−' : '+'}</span>
                <span>{t('video.negativePromptLabel')}</span>
              </button>

              {showNegative && (
                <input
                  type="text"
                  value={negativePrompt}
                  onChange={(e) => setNegativePrompt(e.target.value)}
                  placeholder={t('video.negativePromptPlaceholder')}
                  className="w-full px-3 py-2 rounded-xl border border-surface-border bg-surface-elevated text-foreground text-xs focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                />
              )}
            </div>

            {/* Inspiration Presets */}
            <div className="space-y-2 pt-2 border-t border-surface-border/60">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-foreground-muted">
                  {t('video.samplePresetsTitle')}
                </span>
                <button
                  onClick={() => setShowPresets(!showPresets)}
                  className="text-[11px] text-primary hover:underline"
                >
                  {showPresets ? 'Hide' : 'Show'}
                </button>
              </div>

              {showPresets && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {SAMPLE_PROMPT_PRESETS.map((p) => (
                    <div
                      key={p.id}
                      onClick={() => applyPreset(p)}
                      className="p-2.5 rounded-xl border border-surface-border bg-surface-elevated/40 hover:bg-surface-elevated hover:border-primary/40 transition-all cursor-pointer space-y-1 group"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-foreground group-hover:text-primary transition-colors">
                          {p.title}
                        </span>
                        <span className="text-[10px] text-foreground-muted uppercase font-mono">
                          {p.camera}
                        </span>
                      </div>
                      <p className="text-[11px] text-foreground-muted line-clamp-2">
                        {p.prompt}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Card C: AI Motion Engine */}
          <div className="p-5 rounded-2xl border border-surface-border bg-surface space-y-3 shadow-glass">
            <label className="font-heading font-bold text-sm sm:text-base text-foreground">
              {t('video.modelLabel')}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {VIDEO_MODELS.map((m) => {
                const isSelected = model === m.id;
                return (
                  <div
                    key={m.id}
                    onClick={() => setModel(m.id as VideoModelId)}
                    className={`p-3.5 rounded-xl border cursor-pointer transition-all flex flex-col justify-between space-y-2 ${
                      isSelected
                        ? 'border-primary bg-primary/10 shadow-glow-primary/20 ring-1 ring-primary/40'
                        : 'border-surface-border bg-surface-elevated hover:border-surface-border/80'
                    }`}
                  >
                    <div className="space-y-1">
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-xs font-bold text-foreground">
                          {t(m.titleKey as any)}
                        </span>
                        {m.badge && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold bg-accent/20 text-accent">
                            {m.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-foreground-muted leading-tight">
                        {t(m.descKey as any)}
                      </p>
                    </div>
                    {isSelected && (
                      <div className="flex items-center gap-1 text-[11px] font-semibold text-primary pt-1">
                        <Check className="w-3.5 h-3.5" />
                        <span>Active</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Card D: Virtual Camera Director */}
          <div className="p-5 rounded-2xl border border-surface-border bg-surface space-y-4 shadow-glass">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <SophiaIcon name="camera-360" size={18} className="text-primary shrink-0" />
                <h3 className="font-heading font-bold text-sm sm:text-base text-foreground">
                  {t('video.cameraControlsTitle')}
                </h3>
              </div>
              <p className="text-foreground-muted text-xs">
                {t('video.cameraControlsSubtitle')}
              </p>
            </div>

            {/* Camera Trajectory Buttons */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-foreground-muted">
                {t('video.cameraDirectionLabel')}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {cameraDirections.map((dir) => {
                  const Icon = dir.icon;
                  const isSelected = cameraDirection === dir.id;
                  return (
                    <button
                      key={dir.id}
                      type="button"
                      onClick={() => setCameraDirection(dir.id)}
                      className={`flex items-center gap-2 p-2.5 rounded-xl border text-xs font-medium transition-all ${
                        isSelected
                          ? 'border-primary bg-primary text-primary-fg font-semibold shadow-glow-primary/20'
                          : 'border-surface-border bg-surface-elevated text-foreground-muted hover:text-foreground hover:bg-muted/40'
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{t(dir.labelKey as any)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Motion Intensity Slider */}
            <div className="space-y-2 pt-2 border-t border-surface-border/60">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-foreground-muted">
                  {t('video.cameraMotionIntensity', { level: motionIntensity })}
                </label>
                <span className="text-[11px] font-mono font-semibold text-primary">
                  {motionIntensity <= 3
                    ? t('video.cameraMotionLow')
                    : motionIntensity <= 7
                    ? t('video.cameraMotionMedium')
                    : t('video.cameraMotionHigh')}
                </span>
              </div>
              <input
                type="range"
                min={1}
                max={10}
                step={1}
                value={motionIntensity}
                onChange={(e) => setMotionIntensity(parseInt(e.target.value, 10))}
                className="w-full h-1.5 bg-surface-border rounded-lg appearance-none cursor-pointer accent-primary"
              />
            </div>
          </div>

          {/* Card E: Video Parameters (Duration, Aspect, Loop) */}
          <div className="p-5 rounded-2xl border border-surface-border bg-surface space-y-4 shadow-glass">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Duration */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground-muted">
                  {t('video.durationLabel')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setDuration(5)}
                    className={`p-2.5 rounded-xl border text-xs font-medium flex flex-col items-center justify-center transition-all ${
                      duration === 5
                        ? 'border-primary bg-primary/10 text-primary font-bold ring-1 ring-primary/40'
                        : 'border-surface-border bg-surface-elevated text-foreground-muted hover:text-foreground'
                    }`}
                  >
                    <span>{t('video.duration5s')}</span>
                    <span className="text-[10px] opacity-80">{t('video.cost5s')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDuration(10)}
                    className={`p-2.5 rounded-xl border text-xs font-medium flex flex-col items-center justify-center transition-all ${
                      duration === 10
                        ? 'border-primary bg-primary/10 text-primary font-bold ring-1 ring-primary/40'
                        : 'border-surface-border bg-surface-elevated text-foreground-muted hover:text-foreground'
                    }`}
                  >
                    <span>{t('video.duration10s')}</span>
                    <span className="text-[10px] opacity-80">{t('video.cost10s')}</span>
                  </button>
                </div>
              </div>

              {/* Aspect Ratio */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground-muted">
                  {t('video.aspectRatioLabel')}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['16:9', '9:16', '1:1'] as VideoAspectRatio[]).map((ratio) => (
                    <button
                      key={ratio}
                      type="button"
                      onClick={() => setAspectRatio(ratio)}
                      className={`p-2.5 rounded-xl border text-xs font-medium flex items-center justify-center transition-all ${
                        aspectRatio === ratio
                          ? 'border-primary bg-primary/10 text-primary font-bold ring-1 ring-primary/40'
                          : 'border-surface-border bg-surface-elevated text-foreground-muted hover:text-foreground'
                      }`}
                    >
                      {ratio}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Seamless Infinite Loop Toggle */}
            <div className="flex items-center justify-between p-3 rounded-xl border border-surface-border bg-surface-elevated/40">
              <div className="flex items-center gap-2">
                <Repeat className="w-4 h-4 text-primary" />
                <div>
                  <span className="text-xs font-semibold text-foreground">
                    {t('video.loopLabel')}
                  </span>
                  <p className="text-[11px] text-foreground-muted">
                    {t('video.loopDesc')}
                  </p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={loop}
                onChange={(e) => setLoop(e.target.checked)}
                className="w-4 h-4 accent-primary rounded cursor-pointer"
              />
            </div>
          </div>

          {/* Action Row: Generate Button & Progress */}
          <div className="space-y-3">
            <button
              onClick={() => generateVideo()}
              disabled={isGenerating || !prompt.trim()}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-primary hover:bg-primary/90 text-primary-fg font-heading font-bold text-sm sm:text-base shadow-glow-primary/30 transition-all disabled:opacity-50 active:scale-[0.99] cursor-pointer"
            >
              <SophiaIcon name="camera-360" size={18} className="text-primary-fg shrink-0" />
              <span>
                {isGenerating
                  ? t('video.generatingVideo')
                  : t('video.generateVideoBtn')}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white ml-2">
                <SophiaIcon name="battery" size={12} className="text-white shrink-0" />
                {t('video.creditCostBadge', { credits: requiredCredits })}
              </span>
            </button>

            {isGenerating && (
              <div className="p-4 rounded-xl border border-surface-border bg-surface space-y-2 animate-in fade-in">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-muted truncate">
                    {generationStage}
                  </span>
                  <span className="font-mono font-bold text-primary">
                    {generationProgress}%
                  </span>
                </div>
                <div className="w-full h-2 bg-surface-border rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-300"
                    style={{ width: `${generationProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Active Video Player & Recent Clips Feed (5 cols on lg) */}
        <div className="lg:col-span-5 space-y-6">
          {/* Active Featured Video Player */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-sm sm:text-base text-foreground">
                {t('video.featuredPlayerTitle')}
              </h3>
              {activeVideo && (
                <button
                  onClick={() => handleCopyPrompt(activeVideo.prompt)}
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  {copiedPrompt ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>{t('video.promptCopied')}</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>{t('video.copyPrompt')}</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {activeVideo ? (
              <div className="space-y-3">
                <CustomVideoPlayer video={activeVideo} />

                {/* Prompt & Details Card */}
                <div className="p-4 rounded-xl border border-surface-border bg-surface space-y-2 shadow-glass">
                  <p className="text-xs sm:text-sm text-foreground leading-relaxed">
                    "{activeVideo.prompt}"
                  </p>
                  <div className="flex items-center justify-between pt-1 border-t border-surface-border/60 text-[11px] text-foreground-muted">
                    <span className="font-mono">
                      {new Date(activeVideo.createdAt).toLocaleDateString()}
                    </span>
                    <button
                      onClick={() => deleteVideo(activeVideo.id)}
                      className="text-rose-400 hover:text-rose-300 flex items-center gap-1 transition-colors"
                      title="Delete video from history"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-8 rounded-2xl border border-dashed border-surface-border bg-surface flex flex-col items-center justify-center text-center gap-3">
                <SophiaIcon name="camera-360" size={40} className="text-foreground-muted opacity-40" />
                <div className="space-y-1">
                  <span className="text-sm font-semibold text-foreground">
                    {t('video.noVideosYet')}
                  </span>
                  <p className="text-xs text-foreground-muted max-w-xs">
                    {t('video.noVideosDesc')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Recent Generation Gallery */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-sm sm:text-base text-foreground">
                {t('video.recentGenerationsTitle')}
              </h3>
              <span className="text-xs text-foreground-muted font-mono">
                {generatedVideos.length} clips
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {generatedVideos.map((item) => {
                const isActive = activeVideo?.id === item.id;
                return (
                  <div
                    key={item.id}
                    onClick={() => setActiveVideo(item)}
                    className={`relative aspect-video rounded-xl overflow-hidden border cursor-pointer group transition-all ${
                      isActive
                        ? 'border-primary ring-2 ring-primary/50 shadow-glow-primary/30'
                        : 'border-surface-border hover:border-surface-border/80'
                    }`}
                  >
                    <img
                      src={item.thumbnailUrl}
                      alt={item.prompt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />

                    {/* Play Badge Overlay */}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <div className="w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center backdrop-blur-sm group-hover:scale-110 transition-transform">
                        <Play className="w-3.5 h-3.5 ml-0.5 fill-current" />
                      </div>
                    </div>

                    {/* Duration badge */}
                    <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold bg-black/70 text-white backdrop-blur-sm">
                      {item.duration}s
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
